import React, { memo, useCallback } from 'react'
import { Fade } from 'react-reveal'
import toast from 'react-hot-toast'
import { ExclamationCircleIcon } from '@heroicons/react/24/outline'

import clap from '../images/SARA_Expression/clap.png'
import eye_up from '../images/SARA_Expression/eye_up.png'
import hangry from '../images/SARA_Expression/hangry.png'
import happy from '../images/SARA_Expression/happy.png'
import normal from '../images/SARA_Expression/normal.png'
import sad from '../images/SARA_Expression/sad.png'
import sorry from '../images/SARA_Expression/sorry.png'
import thinking from '../images/SARA_Expression/thinking.png'
import thumb_up from '../images/SARA_Expression/thumb_up.png'
import wink from '../images/SARA_Expression/wink.png'
import worried from '../images/SARA_Expression/worried.png'

/**
 * CUSTOM TOAST WITH ACTIONS BASED ON react-hot-toast
 * @param {{ toastRef: import('react-hot-toast').Toast, children?: JSX.Element, title: string, saraExpression?: 'clap' | 'eye_up' | 'hangry' | 'happy' | 'normal' | 'sad' | 'sorry' | 'thinking' | 'thumb_up' | 'wink' | 'worried', description: string, okAction?: () => void, cancelAction?: () => void }} props 
 * @returns {JSX.Element}
 * @example
 * import toast from 'react-hot-toast'
 * toast.custom( t => <ToastAction toastRef={t} saraExpression="wink" label="Hello" description="My custom toast" okAction={_yourOkFunction} cancelAction={_yourCancelFunction}/>)
 */
function ToastAction({ toastRef, children, label, description, saraExpression, okAction, cancelAction }) {
  const _getExpression = useCallback(() => {
    const expressionPath = {
      clap,
      eye_up,
      hangry,
      happy,
      normal,
      sad,
      sorry,
      thinking,
      thumb_up,
      wink,
      worried
    }

    return expressionPath[saraExpression]
  }, [saraExpression])

  return (
    <Fade top when={toastRef.visible}>
      <div
        className={`max-w-lg w-full ${saraExpression === 'sorry' ? 'bg-red-50 ring-red-100' : 'bg-white ring-black/5'} shadow-lg rounded-lg pointer-events-auto flex ring-1`}
      >
        <div className="flex-1 w-0 p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0 pt-0.5">
              {children || saraExpression && <img src={_getExpression()} className="h-14 w-14" alt={saraExpression} /> || <ExclamationCircleIcon className="h-10 text-pink-800" />}
            </div>
            <div className="ml-3 flex-1">
              <p
                className={`text-sm font-medium ${saraExpression === "sorry" ? "text-red-600" : "text-gray-900"}`}
              >
                {label}
              </p>
              <p
                className={`mt-1 text-sm ${saraExpression === "sorry" ? "text-red-400" : "text-gray-500"}`}
              >
                {description}
              </p>
            </div>
          </div>
        </div>
        {(okAction || cancelAction) && <div className="flex border-l border-gray-200">
          <button
            onClick={() => {
              toast.dismiss(toastRef.id)
              okAction && okAction()
            }}
            className="w-full border border-transparent p-4 flex items-center justify-center text-sm font-medium text-teal-700 hover:text-white focus:outline-none hover:bg-teal-800 transition-colors"
          >
            Oui
          </button>
          <button
            onClick={() => {
              toast.dismiss(toastRef.id)
              cancelAction && cancelAction()
            }}
            className="w-full border-l border-gray-200 rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-pink-700 hover:text-pink-800 hover:bg-pink-50 focus:outline-none transition-colors"
          >
            Non
          </button>
        </div>}
      </div>
    </Fade>
  )
}

export default memo(ToastAction)